import HttpClient from './httpClient';

class CognitoUserController extends HttpClient {
  /**
   * InsertUserInfo inserts a user's info.
   * @param {object} userInfo userInfo of the user
   * @param {string} token Auth token for Auth header
   * @returns {object} response data
   */

  InsertUserInfo = async (userInfo, token) => {
    try {
      const response = await this.client.post('/createUserInfo', userInfo, {
        headers: {
          Authorization: token,
        },
      });
      return response.data;
    } catch (err) {
      throw err;
    }
  };

  /**
   * getUserInfoById get's a user by it's ID.
   * @param {string} userId Id of the user
   * @param {string} token Auth token for Auth header
   * @returns {object} response data
   */

  getUserInfoById = async (userId, token, region) => {
    try {
      const response = await this.client.get(`/getUserInfoById/${userId}`, {
        headers: {
          Authorization: token,
          region,
        },
      });
      return response.data;
    } catch (err) {
      throw err;
    }
  };

  /**
   * addBonusToSubAndRef is a function that adds bonus to both the referrer and the subscriber
   * @param {string} userId userId of the subscriber
   * @param {object} data Data object to add the signUp bonus
   * @param {string} token Auth token to go in Auth header
   * @returns {object} response data
   */

  addBonusToSubAndRef = async (userId, data, token) => {
    try {
      const resp = await this.client.put(
        `/addBonusToSubAndRef/${userId}`,
        data,
        {
          headers: {
            Authorization: token,
          },
        },
      );
      return resp;
    } catch (err) {
      throw err;
    }
  };

  /**
   * updateWiseKoin is a function that updates the wisekoin for the user
   * @param {string} userId userId of the user
   * @param {object} data Data object to update wiseKoin
   * @param {string} token Auth token to go in Auth header
   * @returns {object} response data
   */

  updateWiseKoin = async (userId, data, token) => {
    try {
      const resp = await this.client.put(`/updateWiseKoin/${userId}`, data, {
        headers: {
          Authorization: token,
        },
      });
      return resp;
    } catch (err) {
      throw err;
    }
  };

  /**
   * disableUser is a function that disable the user
   * @param {string} token Auth token to go in Auth header
   * @returns {object} response status of the response
   */
  disableUser = async (token) => {
    try {
      const response = await this.client.delete('/disableUser', {
        headers: {
          Authorization: token,
        },
      });
      return response.status;
    } catch (err) {
      throw err;
    }
  };

  /**
   * InsertUserName inserts a username into userNameDictionary.
   * @param {string} key Key of the username
   * @param {Array} userNames userNames of that key
   * @param {string} token Auth token for Auth header
   * @returns {object} response data
   */

  InsertUserName = async (key, userNames, token) => {
    try {
      const response = await this.client.post(
        `/insertUserName/${key}`,
        userNames,
        {
          headers: {
            Authorization: token,
          },
        },
      );
      return response.data;
    } catch (err) {
      throw err;
    }
  };

  /**
   * getUserName get's a username array by it's key.
   * @param {string} key key of the username
   * @param {string} token Auth token for Auth header
   * @returns {object} response data
   */

  getUserName = async (key, token) => {
    try {
      const response = await this.client.get(`/getUserName/${key}`, {
        headers: {
          Authorization: token,
        },
      });
      return response.data;
    } catch (err) {
      throw err;
    }
  };
  /**
   * getUserInfoByUserName get's a user by it's userName.
   * @param {string} userName userName of the user
   * @param {string} token Auth token for Auth header
   * @returns {object} response data
   */

  getUserInfoByUserName = async (userName, token) => {
    try {
      const response = await this.client.get(
        `/getUserInfoByUserName/${userName}`,
        {
          headers: {
            Authorization: token,
          },
        },
      );
      return response.data;
    } catch (err) {
      throw err;
    }
  };

  /**
   * updateProfileAttributes is a function that updates the custom profile attributes like "organisation" etc. for the user
   * @param {string} userId userId of the user
   * @param {object} data Data object to update attributes
   * @param {string} token Auth token to go in Auth header
   * @returns {object} response data
   */

  updateProfileAttributes = async (userId, data, token) => {
    try {
      const resp = await this.client.put(
        `/updateProfileAttributes/${userId}`,
        data,
        {
          headers: {
            Authorization: token,
          },
        },
      );
      return resp;
    } catch (err) {
      throw err;
    }
  };

  updateTutorialAttributes = async (userId, data, token) => {
    try {
      const resp = await this.client.put(`/tutorials/${userId}`, data, {
        headers: {
          Authorization: token,
        },
      });
      return resp;
    } catch (err) {
      throw err;
    }
  };

  /**
   * checkUniversityLicense is a function that gets the university plan for the user.
   * @param {object} data Data object to make validation for university, like universityCode, userEmai and userId
   * @param {string} token Auth token to go in Auth header
   * @returns {object} response data
   */

  checkUniversityLicense = async (data, token) => {
    try {
      const resp = await this.client.post('/checkUniversityStatus', data, {
        headers: {
          Authorization: token,
        },
      });
      return resp;
    } catch (error) {
      throw error;
    }
  };

  updateUniversityUserCount = async (data, token) => {
    try {
      const resp = await this.client.post('/updateCount', data, {
        headers: {
          Authorization: token,
        },
      });
      return resp;
    } catch (error) {
      throw error;
    }
  };

  updateEmailStatus = async (data, token) => {
    try {
      const resp = await this.client.put('/emailStatus', data, {
        headers: {
          Authorization: token,
        },
      });
      return resp;
    } catch (error) {
      throw error;
    }
  };

  updateUserInfo = async (data, token, userId) => {
    try {
      const resp = await this.client.post(`/updateUserInfo/${userId}`, data, {
        headers: {
          Authorization: token,
        },
      });
      return resp;
    } catch (error) {
      throw error;
    }
  };

  saveListing = async (data, token) => {
    try {
      const resp = await this.client.post(
        `/saveListing`,
        { ...data },
        {
          headers: {
            Authorization: token,
          },
        },
      );
      return resp;
    } catch (error) {
      throw error;
    }
  };

  getListings = async (data, token) => {
    try {
      const resp = await this.client.post(
        `/getOrgListings`,
        { ...data },
        { headers: { Authorization: token } },
      );
      return resp;
    } catch (error) {
      throw error;
    }
  };

  inviteMembers = async (data, token) => {
    try {
      const resp = await this.client.post(
        `/inviteMembers`,
        { ...data },
        { headers: { Authorization: token } },
      );
      return resp;
    } catch (error) {
      throw error;
    }
  };

  getAllApplications = async (data, token) => {
    try {
      const response = await this.client.post(
        `/getOrganizationApplications`,
        data,
        {
          headers: {
            Authorization: token,
          },
        },
      );
      return response.data;
    } catch (e) {
      throw e;
    }
  };

  markSceneTaskboradNotification = async (data, token) => {
    try {
      const response = await this.client.post(
        `/markSceneTaskboradNotification`,
        data,
        {
          headers: {
            Authorization: token,
          },
        },
      );
      return response.data;
    } catch (e) {
      throw e;
    }
  };

  getAllApplicationsOfUser = async (data, token) => {
    try {
      const response = await this.client.post(`/getApplicationsOfUser`, data, {
        headers: {
          Authorization: token,
        },
      });
      return response.data;
    } catch (e) {
      throw e;
    }
  };

  updateOrgSettings = async (data, token) => {
    try {
      const resp = await this.client.post(
        `/updateSettings`,
        { ...data },
        { headers: { Authorization: token } },
      );
      return resp;
    } catch (error) {
      throw error;
    }
  };

  addUsersToOrg = async (data, token) => {
    try {
      const resp = await this.client.post(
        `/addUsers`,
        { ...data },
        { headers: { Authorization: token } },
      );
      return resp.data;
    } catch (error) {
      throw error;
    }
  };

  getNonSSOOrgUsers = async (code, token) => {
    try {
      const resp = await this.client.get(`/getUsers/${code}`, {
        headers: { Authorization: token },
      });
      return resp.data;
    } catch (error) {
      throw error;
    }
  };

  getOrgUsers = async (code, token) => {
    try {
      const resp = await this.client.get(`/getUsers/${code}`, {
        headers: { Authorization: token },
      });
      return resp.data;
    } catch (error) {
      throw error;
    }
  };

  removeNonSSOOrgUsers = async (data, token) => {
    try {
      const resp = await this.client.post(`/removeUsers`, data, {
        headers: { Authorization: token },
      });
      return resp.data;
    } catch (error) {
      throw error;
    }
  };

  bulkUploadUsers = async (data, token) => {
    try {
      const resp = await this.client.post(`/bulk-upload-users`, data, {
        headers: { Authorization: token },
      });
      return resp.data;
    } catch (error) {
      throw error;
    }
  };

  /**
   * checkNonSSOUser is a function that checks if a user is part of non sso organization.
   * @param {object} data code: university code and userId
   * @param {string} token Auth token to go in Auth header
   * @returns {object} true or false
   */

  checkNonSSOUser = async (data, token) => {
    try {
      const resp = await this.client.post('/check-non-sso-user', data, {
        headers: {
          Authorization: token,
        },
      });
      return resp;
    } catch (error) {
      throw error;
    }
  };

  getIrisSettings = async (data, token) => {
    try {
      const resp = await this.client.post(
        `/getIrisSettings`,
        { ...data },
        { headers: { Authorization: token } },
      );
      return resp.data;
    } catch (error) {
      throw error;
    }
  };

  updateIrisSettings = async (data, token) => {
    try {
      const resp = await this.client.post(
        `/updateIrisSettings`,
        { ...data },
        { headers: { Authorization: token } },
      );
      return resp;
    } catch (error) {
      throw error;
    }
  };
}

export default new CognitoUserController(
  process.env.REACT_APP_API_BASE_URL_USER,
);
